<script lang="ts">
  /**
   * StartScreen - Main menu with mode selection
   * First screen users see when launching the game
   */

  import StarField from '../ui/StarField.svelte';

  // Props for navigation callbacks (will be wired up in Story 2.2)
  interface Props {
    onSelectLetterMode?: () => void;
    onSelectMonsterMode?: () => void;
    onOpenSettings?: () => void;
    onOpenAbout?: () => void;
  }

  let {
    onSelectLetterMode = () => {},
    onSelectMonsterMode = () => {},
    onOpenSettings = () => {},
    onOpenAbout = () => {}
  }: Props = $props();
</script>

<div class="start-screen">
  <StarField />

    <!-- About Button -->
    <button
            class="about-button"
            onclick={onOpenAbout}
            aria-label="About this app"
    >
        <span class="about-icon">ℹ️</span>
    </button>

    <!-- Settings Button -->
    <button
            class="settings-button"
            onclick={onOpenSettings}
            aria-label="Open settings"
    >
        <span class="settings-icon">⚙️</span>
    </button>
  <div class="content">
    <!-- Game Title -->
    <h1 class="title">
      <span class="star-icon">🌟</span>
      LETTER SOUNDS
      <span class="star-icon">🌟</span>
    </h1>

    <!-- Mode Selection Buttons -->
    <div class="button-container">
      <button
        class="mode-button letter-mode"
        onclick={onSelectLetterMode}
        aria-label="Play as a letter"
      >
        <span class="icon">🔤</span>
        <span class="text">PLAY AS A LETTER</span>
      </button>

      <button
        class="mode-button monster-mode"
        onclick={onSelectMonsterMode}
        aria-label="Play as a monster"
      >
        <span class="icon">👾</span>
        <span class="text">PLAY AS A MONSTER</span>
      </button>
    </div>

  </div>
</div>

<style>
  .start-screen {
    position: relative;
    width: 100vw;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .content {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-2xl);
    padding: var(--space-lg);
    width: 100%;
    max-width: 600px;
  }

  /* Game Title */
  .title {
    font-size: var(--font-display);
    font-weight: bold;
    text-align: center;
    background: linear-gradient(
      135deg,
      var(--color-cosmic-purple),
      var(--color-bright-yellow)
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0;
    animation: titlePulse 3s ease-in-out infinite;
    line-height: 1.2;
  }

  .star-icon {
    display: inline-block;
    font-size: 0.8em;
    animation: starRotate 4s linear infinite;
  }

  @keyframes titlePulse {
    0%, 100% {
      filter: drop-shadow(0 0 10px rgba(252, 211, 77, 0.5));
    }
    50% {
      filter: drop-shadow(0 0 20px rgba(252, 211, 77, 0.8));
    }
  }

  @keyframes starRotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  /* Button Container */
  .button-container {
    display: flex;
    flex-direction: column;
    gap: var(--space-lg);
    width: 100%;
  }

  /* Mode Buttons */
  .mode-button {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-md);
    width: 100%;
    height: 100px;
    border: none;
    border-radius: 20px;
    font-size: var(--font-lg);
    font-weight: bold;
    color: white;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    animation: buttonFloat 3s ease-in-out infinite;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  /* Letter Mode Button - Blue to Purple Gradient */
  .letter-mode {
    background: linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%);
  }

  .letter-mode:hover {
    box-shadow: 0 0 30px rgba(124, 58, 237, 0.6);
  }

  /* Monster Mode Button - Green to Teal Gradient */
  .monster-mode {
    background: linear-gradient(135deg, #10B981 0%, #14B8A6 100%);
    animation-delay: 0.5s;
  }

  .monster-mode:hover {
    box-shadow: 0 0 30px rgba(16, 185, 129, 0.6);
  }

  /* Button Hover State */
  .mode-button:hover {
    transform: scale(1.05);
  }

  /* Button Active State */
  .mode-button:active {
    transform: scale(0.98);
  }

  .mode-button .icon {
    font-size: 36px;
  }

  .mode-button .text {
    font-size: var(--font-lg);
  }

  @keyframes buttonFloat {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-5px);
    }
  }

  /* About Button */
  .about-button {
    position: absolute;
    top: var(--space-sm);
    right: calc(var(--space-sm) + 75px);
    width: 60px;
    height: 60px;
    border: none;
    border-radius: 50%;
    background: var(--color-space-blue);
    color: white;
    font-size: 28px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ease, background 0.2s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    z-index: 2;
  }

  .about-button:hover {
    transform: scale(1.1);
    background: var(--color-cosmic-purple);
  }

  .about-button:active {
    transform: scale(0.95);
  }

  .about-icon {
    display: block;
  }

  /* Settings Button */
  .settings-button {
    position: absolute;
    top: var(--space-sm);
    right: var(--space-sm);
    width: 60px;
    height: 60px;
    border: none;
    border-radius: 50%;
    background: var(--color-space-blue);
    color: white;
    font-size: 28px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ease, background 0.2s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    z-index: 2;
  }

  .settings-button:hover {
    transform: scale(1.1) rotate(45deg);
    background: var(--color-cosmic-purple);
  }

  .settings-button:active {
    transform: scale(0.95);
  }

  .settings-icon {
    display: block;
  }

  /* Responsive Design */

  /* Mobile Portrait (320px - 767px) */
  @media (max-width: 767px) {
    .title {
      font-size: 36px;
    }

    .mode-button {
      width: 100%;
      max-width: none;
    }

    .mode-button .text {
      font-size: 18px;
    }

    .mode-button .icon {
      font-size: 32px;
    }

    .about-button {
      top: var(--space-xs);
      right: calc(var(--space-xs) + 65px);
      width: 50px;
      height: 50px;
      font-size: 24px;
    }

    .settings-button {
      top: var(--space-xs);
      right: var(--space-xs);
      width: 50px;
      height: 50px;
      font-size: 24px;
    }
  }

  /* Tablet (768px - 1023px) */
  @media (min-width: 768px) and (max-width: 1023px) {
    .mode-button {
      max-width: 320px;
      margin: 0 auto;
    }
  }

  /* Desktop (1024px+) */
  @media (min-width: 1024px) {
    .mode-button {
      max-width: 280px;
    }

    .title {
      font-size: 64px;
    }
  }

  /* Accessibility - Ensure touch targets are large enough */
  @media (pointer: coarse) {
    .mode-button {
      min-height: 80px;
    }

    .about-button {
      min-width: 60px;
      min-height: 60px;
    }

    .settings-button {
      min-width: 60px;
      min-height: 60px;
    }
  }
</style>
